import { supabase } from '@/integrations/supabase/client';

/**
 * Check if a string is a valid UUID
 * 
 * This helper function helps us determine if an ID is a valid Supabase UUID
 * before trying to use it in database operations
 */
export const isValidUuid = (str: string): boolean => {
  // Skip the check if the string contains slashes (likely a file path)
  if (typeof str !== 'string' || str.includes('/')) {
    return false;
  }
  
  // UUID regex pattern
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Extract path from URL for storage operations
 */
export const extractPathFromUrl = (url: string, bucketName: string): string | null => {
  if (!url) return null;
  
  try {
    // Handle case where url is already a path (doesn't contain the full URL)
    if (!url.includes('supabase.co') && !url.startsWith('http')) {
      return url;
    }
    
    const urlObj = new URL(url);
    let path = urlObj.pathname;
    
    // Remove storage API path prefix if present
    const storagePrefix = `/storage/v1/object/public/${bucketName}/`;
    if (path.startsWith(storagePrefix)) {
      path = path.substring(storagePrefix.length);
    }
    
    return decodeURIComponent(path);
  } catch (error) {
    console.error('Failed to extract path from URL:', error);
    return null;
  }
};

/**
 * Creates a properly formatted public URL for a file in Supabase Storage
 */
export const formatStorageUrl = (path: string): string => {
  // Si le chemin est déjà une URL complète, la retourner telle quelle
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Assurez-vous que le chemin ne commence pas par un slash
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  
  // Formater l'URL complète
  return `${supabase.storage.url}/object/public/escort-photos/${cleanPath}`;
};

/**
 * Map des associations profileId -> timestamp pour les photos
 * Ceci permet d'éviter de tester plusieurs formats à chaque fois
 */
export const PROFILE_IMAGE_TIMESTAMPS: Record<string, string> = {
  'e754f13f-dbe1-42e5-9d36-bac230196ee4': '1743374386181',
  '3a25f455-c38a-49dc-ac3a-730790e86c39': '1743374399673',
  '66e75180-fa54-4473-93bd-500a9c07acc9': '1743374406043',
  '32e06d52-ffed-4342-afe2-ce5cc475a992': '1743374412476',
  'e459f4ab-5446-4466-bd49-ad4c3a5ed180': '1743374430423',
  '5509fe5f-9021-4f83-ad6f-66e3c4a27392': '1743374437320',
  '4f07e0be-93d0-45cc-9c62-ff7b6e72ec1c': '1743374445288',
  '70464ee8-2568-4a9c-a71e-c8d9ce7df9e2': '1743374451233'
};

/**
 * Retourne le chemin complet de l'image avec timestamp pour un profileId donné
 */
export const getTimestampedImagePath = (profileId: string): string => {
  const timestamp = PROFILE_IMAGE_TIMESTAMPS[profileId];
  if (timestamp) {
    return `${profileId}/${timestamp}.jpg`;
  }
  return `${profileId}/profile.jpg`; // Fallback au format standard
};
